import { SortDirection } from "components/Lean/SortDirection";
import { createLogger } from "logging";
import { environment } from "models/Environment";
import xss from "xss";

const logger = createLogger("helper.ts");

export const mapErrorsToObject = (errors?: Record<string, string[]>): Record<string, string> => {
  const safeErrors = errors || [];
  const o = Object.assign(
    {},
    ...Object.entries<string[]>(safeErrors).map(([key, errors]) => ({
      [key]: errors.join("\n"),
    }))
  );
  return o;
};

export const mapAlertMessages = (errors: Record<string, string>, knownKeys: string[]): string[] => {
  logger.info("mapAlertMessages(): ", errors, knownKeys);
  const result = Array<string>();
  Object.keys(errors).forEach((key) => {
    const keyLower = key.toLowerCase();
    const isUnknown = !knownKeys.includes(keyLower);
    logger.info("mapAlertMessages(): ", keyLower, isUnknown);
    if (isUnknown) {
      errors[key].split("\n").forEach((x) => {
        if (x) {
          result.push(x);
        }
      });
    }
  });
  return result;
};

class Group<T> {
  key: string;
  members: T[] = [];
  constructor(key: string) {
    this.key = key;
  }
}

export function groupBy<T>(list: T[], func: (x: T) => string): Group<T>[] {
  const res: Group<T>[] = [];
  let group: Group<T> | null = null;

  list.forEach((o) => {
    const groupName = func(o);

    if (group === null) {
      group = new Group<T>(groupName);
    }

    if (groupName !== group.key) {
      res.push(group);
      group = new Group<T>(groupName);
    }

    group.members.push(o);
  });

  if (group != null) {
    res.push(group);
  }

  return res;
}

export const isGuid = (value: string): boolean => {
  const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return pattern.test(value);
};

export const compareNumbers = (
  valueA: number | undefined,
  valueB: number | undefined,
  sortDirection: SortDirection = SortDirection.Ascending
): number => {
  if (valueA === undefined) {
    return valueB === undefined ? 0 : sortDirection;
  } else if (valueB === undefined) {
    return -sortDirection;
  }

  return valueA < valueB ? -sortDirection : valueA > valueB ? sortDirection : 0;
};

export const compareStrings = (
  valueA: string | undefined,
  valueB: string | undefined,
  sortDirection: SortDirection = SortDirection.Ascending
): number => {
  if (valueA === undefined) {
    return valueB === undefined ? 0 : sortDirection;
  } else if (valueB === undefined) {
    return -sortDirection;
  }

  return valueA.localeCompare(valueB) * sortDirection;
};

export const getCompareNumbersFunction = <TItem>(
  getOrder: (item: TItem) => number | undefined,
  sortDirection: SortDirection = SortDirection.Ascending
): ((itemA: TItem, itemB: TItem) => number) => {
  return (itemA, itemB) => compareNumbers(getOrder(itemA), getOrder(itemB), sortDirection);
};

export const getCompareStringsFunction = <TItem>(
  getOrder: (item: TItem) => string | undefined,
  sortDirection: SortDirection = SortDirection.Ascending
): ((itemA: TItem, itemB: TItem) => number) => {
  return (itemA, itemB) => compareStrings(getOrder(itemA), getOrder(itemB), sortDirection);
};

export const getCompareDatesFunction = <TItem>(
  getOrder: (item: TItem) => Date | undefined,
  sortDirection: SortDirection = SortDirection.Ascending
): ((itemA: TItem, itemB: TItem) => number) => {
  return (itemA, itemB) => compareDates(getOrder(itemA), getOrder(itemB), sortDirection);
};

export function validateUrl(url: string): URL | null {
  try {
    const validatedUrl = new URL(url);
    return validatedUrl;
  } catch {
    return null;
  }
}

export const compareDates = (
  dateA: Date | undefined,
  dateB: Date | undefined,
  sortDirection: SortDirection
): number => {
  // Handle undefined values
  if (dateA === undefined && dateB === undefined) return 0;
  if (dateA === undefined) return sortDirection === SortDirection.Ascending ? 1 : -1;
  if (dateB === undefined) return sortDirection === SortDirection.Ascending ? -1 : 1;

  // Compare the date values
  const timeA = dateA.getTime();
  const timeB = dateB.getTime();

  return sortDirection === SortDirection.Ascending ? timeA - timeB : timeB - timeA;
};

export function stripHtmlTags(html: string | null | undefined): string {
  if (!html) {
    return "";
  }
  const safeHtml = xss(html);
  const doc = new DOMParser().parseFromString(safeHtml, "text/html");
  return doc.body.textContent || "";
}

export function getCleanedPath(path: string): string {
  // Remove all '/' at the start
  return path.replace(/^\/+/, "");
}

export function getUrlWithoutHttps(path: string): string {
  return path.replace("https://", "");
}

export function getTruncatedText(text: string, maxLength: number): string {
  return text.length > maxLength ? text.substring(0, maxLength - 3) + "..." : text;
}

export function getExternalSiveLink(path: string): string {
  if (!path || path.startsWith("http")) {
    return path;
  }
  let splittedPath = path.split(":");
  return `${environment.siveUrl}/${splittedPath[0]}/meetings/${splittedPath[1]}`;
}
